import styled from '@emotion/styled'
import cx from 'classnames'

import { useCallback, useState, type FormEventHandler } from 'react'

import { Button } from '@/components/Button'
import {
  Container,
  type BackgroundColor,
  type Space,
  type ContainerProps,
} from '@/components/Container'
import { FormMessage } from '@/components/FormMessage'
import { InputWithMaskValidation } from '@/components/Input'
import { Display, HeadingL, BodyP, type TypographyColorModifier } from '@/components/Typography'
import type { TitleProps } from '@/models'
import { CONFIG } from '@/styles/emotion.config'

import { builderLightText } from '../utils'

export interface CustomFormProps {
  id?: string
  title: TitleProps
  subtitle: TitleProps
  fields: {
    label?: string
    name?: string
    type?: string
    placeholder?: string
    pattern?: string
    mask?: string
    errorMessage?: string
  }[]
  button: {
    label: string
  }
  background: BackgroundColor
  backgroundColor?: string
  colorModifier: TypographyColorModifier
  size: 'm' | 'l'
  space: Space
  xhrUrl: string
  formSuccessMessage?: string
  formErrorMessage?: string
  observations?: string
  submitRedirectUrl?: string
}

const CustomFormStyles = styled.div({
  '& .form': {
    padding: '2.625rem',
  },

  '& .form form': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  '& .form__input': {
    marginBottom: '1rem',
  },

  '& .form__disclaimer': {
    marginTop: '1rem',
    fontWeight: '400',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    letterSpacing: 'normal',
    webkitFontSmoothing: 'antialiased',
  },

  '& .form__disclaimer a': {
    color: 'white',
    fontStyle: 'normal',
    textDecoration: 'none',
    fontWeight: 'bold',
    borderBottom: '#fff solid 3px',
  },

  '& .form__disclaimer--light': {
    color: 'white',
  },

  '& .form__submit': {
    width: '100%',
  },

  [CONFIG.media.lg]: {
    '& .form__title, & .form__disclaimer': {
      textAlign: 'center',
    },
  },
})

const CustomForm = ({
  id,
  title,
  subtitle,
  formSuccessMessage,
  formErrorMessage,
  fields,
  button: { label: buttonLabel },
  xhrUrl,
  submitRedirectUrl,
  observations,
  size,
  space,
  background,
  backgroundColor,
  colorModifier,
}: CustomFormProps) => {
  const [state, setState] = useState({
    success: false,
    error: false,
  })

  const TitleComponent = size === 'm' ? Display : HeadingL
  const lightText = builderLightText(colorModifier)
  const containerProps: ContainerProps = {
    className: 'form',
    spaceIn: size,
    spaceOut: space,
    backgroundColor: background,
    backgroundColorHex: backgroundColor,
    id,
  }

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (event) => {
      event.preventDefault()

      const data = new FormData()
      const inputs = event.currentTarget.querySelectorAll('input') || []
      for (const input of inputs) {
        if (!input.validity.valid) {
          return
        }

        data.append(input.name, input.value)
      }

      try {
        const response = await fetch(xhrUrl, { method: 'post', body: data })

        if (!response.ok) {
          throw response
        }

        setState({ success: true, error: false })

        if (submitRedirectUrl) {
          window.open(submitRedirectUrl, '_blank')
        }
      } catch (error) {
        setState({ error: true, success: false })
      }

      setTimeout(() => {
        setState({ error: false, success: false })
      }, 5000)
    },
    [submitRedirectUrl, xhrUrl],
  )

  return (
    <CustomFormStyles>
      <Container {...containerProps}>
        <div className="eu-sm-4">
          <TitleComponent
            dark="high"
            light={lightText}
            className="form__title eu-sm-2"
            tag={title.tag || 'h1'}
          >
            {title.text}
          </TitleComponent>
          <BodyP light={lightText} tag={subtitle.tag || 'p'}>
            {subtitle.text}
          </BodyP>
        </div>

        {fields?.length && (
          <>
            <form onSubmit={handleSubmit} noValidate>
              <fieldset>
                <div className="message-box">
                  <FormMessage type="success" show={state.success} message={formSuccessMessage} />
                  <FormMessage type="error" show={state.error} message={formErrorMessage} />
                </div>

                {fields.map((field, key) => (
                  <InputWithMaskValidation
                    {...field}
                    key={key}
                    hasLightText={!!lightText}
                    className="form__input"
                  />
                ))}

                <Button
                  type="submit"
                  className="form__submit"
                  size="lg"
                  modifier={lightText ? 'solid-invert' : 'solid'}
                >
                  {buttonLabel}
                </Button>
              </fieldset>
            </form>

            {observations && observations.length && (
              <div
                className={cx(`form__disclaimer`, {
                  'form__disclaimer--light': lightText,
                })}
                dangerouslySetInnerHTML={{ __html: observations }}
              />
            )}
          </>
        )}
      </Container>
    </CustomFormStyles>
  )
}

export default CustomForm
